/* eslint-disable check-file/filename-naming-convention */
import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { MedicalConfirmationFlowEnum } from "@/components/with-undiagnosed-symptoms";
import {
  QUOTE_JOURNEY_TYPE,
  QUOTE_CHANNEL_ONLINE,
  Individual,
} from "@/config/app-constant";
import { PRODUCT_DOMAIN } from "@/constants";
import axioInstance from "@/libs/axios";
import { useAppDispatch, useAppSelector } from "@/store/hook";
import { updateQuoteOnQuestion } from "@/store/slices/quote/quote.action";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { MedicalGateKeeperQuestions } from "@/types/MedicalTypes";
import { QuoteData, QuotePayload } from "@/types/quote/quote-data";
import logError from "@/utils/logger";
import { generateOrganizerAsTraveler } from "@/utils/traveller.utils";

interface AddMedicalProps {
  referenceId?: string;
  version?: string;
  patchData: MedicalGateKeeperQuestions;
  triggerUpdate: boolean;
  viewPage?: string;
  resetTrigger: () => void;
  experimentDetails?: Record<string, MedicalConfirmationFlowEnum> | null;
}

export default function useSaveGateKeeperAnswers({
  referenceId,
  version,
  patchData,
  triggerUpdate,
  resetTrigger,
  viewPage,
  experimentDetails = null,
}: AddMedicalProps) {
  const [gkData, setMedicalResponse] = useState(null);
  const [gkLoading, setLoading] = useState<boolean>(false);
  const [gkError, setError] = useState<null | AxiosError>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const quote = useAppSelector((state: RootState) => state.quote);

  const url = `${process.env.NEXT_PUBLIC_BASE_URL}/quote/${referenceId}/updateGateKeeperAnswers`;
  const postBody: {
    data: {
      type: string;
      channel: string;
      version?: string;
      viewPage?: string;
      attributes: {
        domain: string;
        travellerDetails: {
          gateKeeperAnswers: MedicalGateKeeperQuestions;
        };
        experimentDetails?: Record<string, MedicalConfirmationFlowEnum> | null;
      };
    };
  } = {
    data: {
      type: QUOTE_JOURNEY_TYPE,
      channel: QUOTE_CHANNEL_ONLINE,
      version,
      viewPage,
      attributes: {
        domain: PRODUCT_DOMAIN,
        travellerDetails: {
          gateKeeperAnswers: patchData,
        },
      },
    },
  };

  const updateQuoteState = () => {
    if (!referenceId && !patchData) return;

    const newQuote = {
      ...quote,
      attributes: {
        ...quote.attributes,
        travellerDetails: {
          ...quote.attributes.travellerDetails,
          gateKeeperAnswers: patchData,
        },
      },
    };

    dispatch(updateQuote(newQuote as QuoteData));
  };

  useEffect(() => {
    if (!triggerUpdate) return;
    const patchGateKeeperAnswers = async () => {
      try {
        setLoading(true);

        // update experiment details to post body
        if (experimentDetails) {
          postBody.data.attributes.experimentDetails = experimentDetails;
        }
        const response = await axioInstance.patch(url, postBody);

        if (response.data.code === 200) {
          updateQuoteState();
        }

        setMedicalResponse(response.data);
      } catch (err) {
        const axioErr = err as AxiosError;
        setError(axioErr);
        logError(axioErr, { location: "Traveller health page submit" });
      } finally {
        setLoading(false);
        resetTrigger();
      }
    };

    patchGateKeeperAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerUpdate]);

  const updateTravelerDetails = async (
    questionData: QuotePayload,
    newMedicalFlowEnabled: boolean = false,
    isRenewalMedicalEditFlow?: boolean
  ) => {
    let newQuestionData = questionData;
    if (!isRenewalMedicalEditFlow) {
      const inIndividualOrganizerTravelling: boolean =
        newMedicalFlowEnabled &&
        !!questionData.attributes?.organiser?.travelling &&
        questionData.attributes?.tripDetails?.partyType === Individual;

      const newTravelers = generateOrganizerAsTraveler(
        inIndividualOrganizerTravelling
          ? []
          : questionData.attributes?.travellerDetails?.traveller,
        questionData.attributes.organiser ?? undefined
      );

      if (newTravelers) {
        newQuestionData = {
          ...questionData,
          attributes: {
            ...questionData.attributes,
            travellerDetails: {
              ...questionData.attributes.travellerDetails,
              traveller: newTravelers,
            },
          },
        };

        sessionStorage.removeItem("initialLoad");
        sessionStorage.removeItem("organiserTravellerId");
      }
    }

    try {
      setLoading(true);
      const response = await axioInstance.patch(url, postBody);
      await dispatch(
        updateQuoteOnQuestion({ payload: newQuestionData, dispatch, router })
      );

      setMedicalResponse(response.data);
    } catch (err) {
      const axioErr = err as AxiosError;
      setError(axioErr);
      logError(axioErr, { location: "Traveller health page submit" });
    } finally {
      setLoading(false);
    }
  };

  const updateTravelerDetailsInQuoteCreate = async () => {
    const quoteData = {
      type: QUOTE_JOURNEY_TYPE,
      channel: QUOTE_CHANNEL_ONLINE,
      version,
      referenceId,
      attributes: {
        domain: PRODUCT_DOMAIN,
        lastVisitedPage: "test",
        organiser: quote.attributes?.organiser,
        tripDetails: quote.attributes?.tripDetails,
        marketingConsent: quote.attributes?.marketingConsent,
        travellerDetails: {
          ...quote.attributes.travellerDetails,
          gateKeeperAnswers: patchData,
        },
      },
    };

    try {
      setLoading(true);
      const response = await axioInstance.patch(url, postBody);
      await dispatch(
        updateQuoteOnQuestion({
          payload: quoteData as QuotePayload,
          dispatch,
          router,
        })
      );

      setMedicalResponse(response.data);
    } catch (err) {
      const axioErr = err as AxiosError;
      setError(axioErr);
      logError(axioErr, { location: "Traveller health page submit" });
    } finally {
      setLoading(false);
    }
  };

  const updateTravelersData = async () => {
    const updatedQuoteData = {
      ...quote,
      attributes: {
        ...quote.attributes,
        travellerDetails: {
          ...quote.attributes.travellerDetails,
          traveller: quote.attributes?.travellerDetails?.traveller || [],
        },
      },
    };

    try {
      setLoading(true);
      await dispatch(
        updateQuoteOnQuestion({
          payload: updatedQuoteData as QuotePayload,
          dispatch,
          router,
        })
      );
    } catch (err) {
      const axioErr = err as AxiosError;
      setError(axioErr);
      logError(axioErr, { location: "Medical edit traveller update" });
    } finally {
      setLoading(false);
    }
  };

  return {
    gkData,
    gkLoading,
    gkError,
    updateTravelerDetails,
    updateTravelerDetailsInQuoteCreate,
    updateTravelersData,
  };
}
